<template lang="pug">
.checklist-form-field
  .checklist-form-field__grid
    string-field.checklist-form-field__item(
      :initialValue="localField.title",
      :required="true",
      :invalid="Boolean(errors.title)",
      :errorMessage="notifies.not_empty",
      :label="checkliststLocales.title",
      :placeholder="checkliststLocales.field_title",
      @change="changeFieldTitle"
    )
    string-field.checklist-form-field__item(
      :initialValue="localField.instruction",
      :label="checkliststLocales.details",
      :placeholder="checkliststLocales.what_can_help",
      @change="changeFieldInstruction"
    )

  .checklist-form-field__required
    checkbox-field(
      :initialValue="Boolean(localField.required)",
      :title="checkliststLocales.required_subfield",
      @change="changeFieldRequired"
    )

  .checklist-form-field__grid.checklist-form-field__grid--bottom
    select-field.checklist-form-field__item(
      :initialValue="localField.type",
      :options="fieldTypeOptions",
      :showCancel="false",
      :label="checkliststLocales.field_type",
      :hint="fieldTypeHint",
      :placeholder="checkliststLocales.select_field_type",
      :openDirection="null",
      @change="changeFieldType"
    )
  .checklist-form-field__grid.checklist-form-field__grid--range(v-if="showRangeBlock")
    float-field.checklist-form-field__item(
      v-if="showNumericRangeBlock",
      :initialValue="localField.min_value",
      :label="minValueLabel",
      :hint="minValueHint",
      :max="localField.max_value",
      step="0.01",
      @change="changeFieldMinValue"
    )
    float-field.checklist-form-field__item(
      v-if="showNumericRangeBlock",
      :initialValue="localField.max_value",
      :label="maxValueLabel",
      :hint="maxValueHint",
      :min="localField.min_value",
      step="0.01",
      @change="changeFieldMaxValue"
    )
    date-field.checklist-form-field__item(
      v-if="showDateRangeBlock",
      :value="localField.min_value || ''",
      :label="minDateLabel",
      :hint="minValueHint",
      :type="localField.type",
      :maxDate="localField.max_value || ''",
      :withWrapper="true",
      @select-date="changeFieldMinValue"
    )
    date-field.checklist-form-field__item(
      v-if="showDateRangeBlock",
      :value="localField.max_value || ''",
      :label="maxDateLabel",
      :hint="maxValueHint",
      :type="localField.type",
      :minDate="localField.min_value || ''",
      :withWrapper="true",
      @select-date="changeFieldMaxValue"
    )
  .checklist-form-field__options(v-if="showOptionsBlock")
    .checklist-form-field__options-head
      .checklist-form-field__options-title {{ currentFieldTypeTitle }}
      .checklist-form-field__options-deviation-title
        span {{ deviationLabel }}
        hint-component(v-if="optionDeviationHint", :text="optionDeviationHint")
      .checklist-form-field__options-delete-title
    .checklist-form-field__options-list
      .checklist-form-field__option-row(v-for="option in sortedOptions", :key="getOptionKey(option)")
        string-field.checklist-form-field__option(
          :initialValue="option.title",
          :invalid="showOptionInvalid(option.order)",
          :errorMessage="notifies.not_empty",
          :placeholder="checkliststLocales.enter_option",
          @change="val => changeOptionValue(option.order, val)"
        )
        .checklist-form-field__option-deviation-wrap
          q-checkbox.checklist-form-field__option-deviation.form-checkbox(
            dark,
            :value="Boolean(option.deviation)",
            @input="val => changeOptionDeviation(option.order, val)"
          )
        .checklist-form-field__delete-option-wrap
          button.checklist-form-field__delete-option-button(
            type="button",
            :aria-label="checkliststLocales.delete",
            @click="deleteOption(option.order)"
          )
            q-icon(name="delete", size="20px")
      button.checklist-form-field__add-option-button(type="button", @click="addEmptyOption")
        span {{ checkliststLocales.another_option }}
</template>

<script setup>
import { computed, ref, watch } from "vue";
import { format, isValid, parse } from "date-fns";
import i18n from "@/plugins/vue-i18n";
import checkboxField from "../../../shared/general_components/fields/checkboxField.vue";
import dateField from "../../../shared/general_components/fields/dateField.vue";
import floatField from "../../../shared/general_components/fields/floatField.vue";
import hintComponent from "../../../shared/helpers/hint.vue";
import selectField from "../../../shared/general_components/fields/selectField.vue";
import stringField from "../../../shared/general_components/fields/stringField.vue";
import { formatDateText } from "@/services/useDateTextFormatter";
import { checkliststLocales, currentLocale, notifies } from "@/services/useLocales";

const props = defineProps({
  /** Проп текущего поля страницы чеклиста. */
  field: { type: Object, default: () => ({}) },
  /** Ошибки валидации текущего поля. */
  errors: { type: Object, default: () => ({}) },
});

const emit = defineEmits([
  /** Эмит изменения поля страницы чеклиста. */
  "change-field",
]);

const fieldTypesWithOptions = ["checkbox", "select", "radio"];
const fieldTypesWithDateRange = ["date", "datetime"];

const normalizeNumericValue = value => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  const preparedValue = Number(String(value).replace(",", "."));
  return Number.isNaN(preparedValue) ? null : preparedValue;
};

const normalizeDateValue = (value, type) => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  const stringValue = String(value);

  if (type === "datetime") {
    return formatDateText(stringValue, "dd.MM.yyyy HH:mm");
  }

  const isDateOnly = type === "date" && /^\d{4}-\d{2}-\d{2}$/.test(stringValue);

  if (!isDateOnly && !stringValue.includes("T")) {
    return stringValue;
  }

  const date = isDateOnly ? parse(stringValue, "yyyy-MM-dd", new Date()) : new Date(stringValue);

  if (!isValid(date)) {
    return stringValue;
  }

  return format(date, "dd.MM.yyyy");
};

const normalizeRangeValue = (value, type) => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  if (type === "float") {
    return normalizeNumericValue(value);
  }

  if (fieldTypesWithDateRange.includes(type)) {
    return normalizeDateValue(value, type);
  }

  return value;
};

const parseRangeDateValue = (value, type) => {
  if (!fieldTypesWithDateRange.includes(type) || !value) {
    return null;
  }

  const dateFormat = type === "datetime" ? "dd.MM.yyyy HH:mm" : "dd.MM.yyyy";
  const parsedDate = parse(String(value), dateFormat, new Date());

  if (!Number.isNaN(parsedDate.getTime())) {
    return parsedDate;
  }

  if (type === "datetime") {
    const legacyParsedDate = parse(String(value), "dd.MM.yyyy, HH:mm", new Date());

    if (!Number.isNaN(legacyParsedDate.getTime())) {
      return legacyParsedDate;
    }
  }

  const fallbackDate = parse(String(value), "dd.MM.yyyy", new Date());
  return Number.isNaN(fallbackDate.getTime()) ? null : fallbackDate;
};

const isMaxValueLessThanMinValue = (minValue, maxValue, type) => {
  if (minValue === null || minValue === undefined || maxValue === null || maxValue === undefined) {
    return false;
  }

  if (type === "float") {
    return Number(maxValue) < Number(minValue);
  }

  if (fieldTypesWithDateRange.includes(type)) {
    const minDate = parseRangeDateValue(minValue, type);
    const maxDate = parseRangeDateValue(maxValue, type);

    return Boolean(minDate && maxDate && maxDate < minDate);
  }

  return false;
};

const normalizeOptions = options => {
  if (!Array.isArray(options)) {
    return [];
  }

  return options.map((option, index) => {
    if (typeof option === "string") {
      return { title: option, order: index + 1, deviation: false };
    }

    return {
      id: option?.id,
      title: option?.title ?? "",
      order: option?.order ?? index + 1,
      deviation: Boolean(option?.deviation),
    };
  });
};

const normalizeField = field => {
  const fieldType = field?.type ?? "string";
  const normalizedField = {
    ...field,
    title: field?.title ?? "",
    instruction: field?.instruction ?? "",
    type: fieldType,
    required: Boolean(field?.required),
    min_value: normalizeRangeValue(field?.min_value, fieldType),
    max_value: normalizeRangeValue(field?.max_value, fieldType),
    options: normalizeOptions(field?.options),
  };

  if (fieldTypesWithOptions.includes(normalizedField.type) && normalizedField.options.length === 0) {
    normalizedField.options = [{ title: "", order: 1 }];
  }

  if (isMaxValueLessThanMinValue(normalizedField.min_value, normalizedField.max_value, normalizedField.type)) {
    normalizedField.max_value = normalizedField.min_value;
  }

  return normalizedField;
};

const localField = ref(normalizeField(props.field));

watch(
  () => props.field,
  newField => {
    localField.value = normalizeField(newField);
  },
  { deep: true },
);

const emitFieldChange = () => {
  emit("change-field", {
    ...localField.value,
    options: normalizeOptions(localField.value.options),
  });
};

const fieldTypeOptions = computed(() => [
  { id: "string", title: checkliststLocales.value.text_field },
  { id: "float", title: checkliststLocales.value.float_field },
  { id: "select", title: checkliststLocales.value.dropdown_field },
  { id: "date", title: checkliststLocales.value.date_field },
  { id: "datetime", title: checkliststLocales.value.datetime_field },
  { id: "checkbox", title: checkliststLocales.value.checkbox_field },
  { id: "radio", title: checkliststLocales.value.radio_field },
  { id: "file", title: checkliststLocales.value.file_field },
  { id: "comments", title: checkliststLocales.value.comments },
  { id: "text", title: checkliststLocales.value.text_field },
]);
const deviationLabel = computed(() => i18n["messages"][currentLocale.value]["deviation"]);
const optionDeviationHint = computed(() =>
  localField.value.type === "checkbox"
    ? checkliststLocales.value?.checkbox_deviation_hint ?? ""
    : checkliststLocales.value?.option_deviation_hint ?? "",
);
const minValueHint = computed(() => checkliststLocales.value?.min_value_deviation_hint ?? "");
const maxValueHint = computed(() => checkliststLocales.value?.max_value_deviation_hint ?? "");
const commentsDeviationHint = computed(() => checkliststLocales.value?.comments_deviation_hint ?? "");
const fieldTypeHint = computed(() => (localField.value.type === "comments" ? commentsDeviationHint.value : ""));
const minValueLabel = computed(() => (currentLocale.value === "ru" ? "Минимальное значение" : "Min value"));
const maxValueLabel = computed(() => (currentLocale.value === "ru" ? "Максимальное значение" : "Max value"));
const minDateLabel = computed(() =>
  currentLocale.value === "ru"
    ? localField.value.type === "datetime"
      ? "Минимальные дата и время"
      : "Минимальная дата"
    : localField.value.type === "datetime"
    ? "Min date and time"
    : "Min date",
);
const maxDateLabel = computed(() =>
  currentLocale.value === "ru"
    ? localField.value.type === "datetime"
      ? "Максимальные дата и время"
      : "Максимальная дата"
    : localField.value.type === "datetime"
    ? "Max date and time"
    : "Max date",
);
const showNumericRangeBlock = computed(() => localField.value.type === "float");
const showDateRangeBlock = computed(() => fieldTypesWithDateRange.includes(localField.value.type));
const showRangeBlock = computed(() => showNumericRangeBlock.value || showDateRangeBlock.value);
const showOptionsBlock = computed(() => fieldTypesWithOptions.includes(localField.value.type));
const sortedOptions = computed(() =>
  [...(localField.value.options ?? [])].sort(
    (firstOption, secondOption) => (firstOption.order ?? 0) - (secondOption.order ?? 0),
  ),
);
const currentFieldTypeTitle = computed(() => {
  const selectedOption = fieldTypeOptions.value.find(option => option.id === localField.value.type);

  return selectedOption?.title ?? checkliststLocales.value.subfield_title;
});
const firstOptionOrder = computed(() => sortedOptions.value[0]?.order ?? 1);

const changeFieldTitle = val => {
  localField.value.title = val?.value ?? "";
  emitFieldChange();
};

const changeFieldInstruction = val => {
  localField.value.instruction = val?.value ?? "";
  emitFieldChange();
};

const changeFieldRequired = val => {
  localField.value.required = Boolean(val?.value);
  emitFieldChange();
};

const changeFieldType = val => {
  localField.value.type = val?.value ?? "string";
  localField.value.options = [];
  localField.value.min_value = null;
  localField.value.max_value = null;

  if (fieldTypesWithOptions.includes(localField.value.type)) {
    localField.value.options = [{ title: "", order: 1, deviation: false }];
  }

  emitFieldChange();
};

const addEmptyOption = () => {
  const options = normalizeOptions(localField.value.options);
  const lastOrder = Math.max(0, ...options.map(option => option.order ?? 0));

  localField.value.options = [
    ...options,
    {
      title: "",
      order: lastOrder + 1,
      deviation: false,
    },
  ];
  emitFieldChange();
};

const changeOptionValue = (optionOrder, val) => {
  const options = normalizeOptions(localField.value.options);
  const option = options.find(currentOption => currentOption.order === optionOrder);

  if (!option) {
    return;
  }

  option.title = val?.value ?? "";
  localField.value.options = options;
  emitFieldChange();
};

const changeOptionDeviation = (optionOrder, val) => {
  const options = normalizeOptions(localField.value.options);
  const option = options.find(currentOption => currentOption.order === optionOrder);

  if (!option) {
    return;
  }

  option.deviation = Boolean(val);
  localField.value.options = options;
  emitFieldChange();
};

const changeFieldMinValue = val => {
  const nextMinValue = normalizeRangeValue(val?.value ?? val, localField.value.type);
  localField.value.min_value = nextMinValue;

  if (isMaxValueLessThanMinValue(nextMinValue, localField.value.max_value, localField.value.type)) {
    localField.value.max_value = nextMinValue;
  }

  emitFieldChange();
};

const changeFieldMaxValue = val => {
  const nextMaxValue = normalizeRangeValue(val?.value ?? val, localField.value.type);
  localField.value.max_value = isMaxValueLessThanMinValue(
    localField.value.min_value,
    nextMaxValue,
    localField.value.type,
  )
    ? localField.value.min_value
    : nextMaxValue;
  emitFieldChange();
};

const deleteOption = optionOrder => {
  localField.value.options = normalizeOptions(localField.value.options)
    .filter(option => option.order !== optionOrder)
    .map((option, index) => ({
      ...option,
      order: index + 1,
    }));
  emitFieldChange();
};

const getOptionKey = option => `checklist-field-option-${option.order}`;
const showOptionInvalid = optionOrder => Boolean(props.errors?.options) && optionOrder === firstOptionOrder.value;
</script>

<style scoped lang="scss">
.checklist-form-field {
  width: 100%;
  padding: 20px;
  border: 1px solid var(--checklist-section-border);
  border-radius: 12px;
  background: var(--simple-table-background);

  &__grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 20px 24px;

    &--bottom {
      align-items: start;
    }
  }

  &__required {
    margin: 20px 0;
  }

  &__item {
    position: relative;
  }

  &__options-title {
    color: var(--form-field-label);
    font-size: 14px;
    font-style: normal;
    font-weight: 600;
    line-height: 27px;
    padding: 1px;
  }

  &__options {
    margin-top: 20px;
  }

  &__options-head {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 132px 36px;
    gap: 12px;
    margin-bottom: 6px;
    align-items: end;
  }

  &__options-deviation-title {
    color: var(--form-field-label);
    font-size: 14px;
    font-style: normal;
    font-weight: 600;
    line-height: 27px;
    padding: 1px;
    text-align: center;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
  }

  &__options-delete-title {
    min-height: 1px;
  }

  &__options-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  &__option-row {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 132px 36px;
    gap: 12px;
    align-items: start;
  }

  &__option {
    :deep(.form-field) {
      margin-bottom: 0;
    }

    :deep(.valid-error-message) {
      position: static;
      margin-top: 0;
      padding-left: 0;
    }
  }

  &__delete-option-wrap {
    height: var(--field-min-height);
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__option-deviation-wrap {
    height: var(--field-min-height);
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__option-deviation {
    margin: 0;

    :deep(.q-checkbox__inner--truthy) {
      color: var(--checkbox-checked);
    }

    :deep(.q-checkbox__inner--falsy) {
      color: var(--checkbox-unchecked);
    }

    :deep(.q-checkbox__label) {
      font-size: 13px;
      font-weight: 600;
      color: var(--modal-form-card-color);
    }
  }

  &__delete-option-button {
    width: 36px;
    height: 36px;
    padding: 0;
    border: 0;
    border-radius: 10px;
    background: transparent;
    color: var(--modal-form-card-color);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: opacity 0.2s ease, background-color 0.2s ease;

    &:hover {
      opacity: 0.75;
      background: var(--checklist-action-hover-background);
    }
  }

  &__add-option-button {
    margin-top: 12px;
    padding: 0;
    border: 0;
    background: transparent;
    color: var(--checklist-accent-color);
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;

    &:hover {
      opacity: 0.75;
    }
  }

  @media (width <= 900px) {
    &__grid {
      grid-template-columns: 1fr;
    }

    &__options-head,
    &__option-row {
      grid-template-columns: minmax(0, 1fr) 72px 36px;
    }
  }
}
</style>
